/**
 * Resilient remote loader for Konitys federated modules.
 *
 * Uses dynamic import() to load Vite federation ES modules.
 *
 * - Timeout: 5 seconds per attempt
 * - Retry: 1 retry before giving up
 * - Fallback: returns a fallback component instead of throwing
 * - Auto-reconnect: retries in background every 30s when failed
 */

import React from 'react';
import ReactDOM from 'react-dom';

const DEFAULT_REMOTE_URL =
  process.env.REACT_APP_PLATEFORM_URL ||
  'https://konitys-hub-prod.up.railway.app';

const REMOTE_ENTRY_PATH = '/assets/remoteEntry.js';
const LOAD_TIMEOUT_MS = 5000;
const MAX_RETRIES = 1;
const RECONNECT_INTERVAL_MS = 30000;

let container = null;
let loadFailed = false;
let reconnectTimer = null;
let onReconnect = null;

/**
 * Native dynamic import that bypasses webpack bundling.
 * Using new Function() ensures webpack cannot transform the import() call
 * into __webpack_require__(), which would break cross-origin ES module loading.
 */
// eslint-disable-next-line no-new-func
const nativeImport = new Function('url', 'return import(url)');

/**
 * Dynamic import with timeout.
 */
function importWithTimeout(url, timeoutMs) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`Timeout loading ${url} after ${timeoutMs}ms`));
    }, timeoutMs);

    nativeImport(url)
      .then((mod) => {
        clearTimeout(timer);
        resolve(mod);
      })
      .catch((err) => {
        clearTimeout(timer);
        reject(err);
      });
  });
}

/**
 * Initialize the remote container (shared deps handshake).
 */
async function initContainer() {
  if (container) return container;

  const remoteUrl = DEFAULT_REMOTE_URL;
  // Cache-bust remoteEntry.js — filename has no hash, so browsers/proxies
  // can serve a stale manifest indefinitely.  Rotate key every 5 minutes.
  const cacheBuster = Math.floor(Date.now() / 300_000);
  const entryUrl = `${remoteUrl}${REMOTE_ENTRY_PATH}?v=${cacheBuster}`;

  const remoteModule = await importWithTimeout(entryUrl, LOAD_TIMEOUT_MS);

  if (!remoteModule || !remoteModule.get || !remoteModule.init) {
    throw new Error('Remote module does not export get/init');
  }

  container = remoteModule;

  // Share the host app's React so federated components use the same instance.
  // Without this, hooks crash: the remote uses its own bundled React whose
  // ReactCurrentDispatcher is null (not set up by the host's ReactDOM).
  const shareScope = {
    react: {
      [React.version]: {
        get: () => Promise.resolve(() => React),
      },
    },
    'react-dom': {
      [ReactDOM.version]: {
        get: () => Promise.resolve(() => ReactDOM),
      },
    },
  };
  await container.init(shareScope);

  return container;
}

/**
 * Start background reconnection polling.
 * When the Hub comes back, calls onReconnect so the app can re-render.
 */
function startReconnect() {
  if (reconnectTimer) return;
  reconnectTimer = setInterval(async () => {
    try {
      // Reset state for retry
      container = null;
      await initContainer();
      // Success — Hub is back
      loadFailed = false;
      clearInterval(reconnectTimer);
      reconnectTimer = null;
      console.info('[Konitys Federation] Hub reconnected');
      if (onReconnect) onReconnect();
    } catch {
      // Still down, keep polling
    }
  }, RECONNECT_INTERVAL_MS);
}

/**
 * Set a callback that fires when the Hub reconnects after a failure.
 * Typically used to trigger a React re-render / state update.
 */
export function setReconnectCallback(cb) {
  onReconnect = cb;
}

/**
 * Load a remote module with retry logic.
 * Returns the module or null if all attempts fail.
 */
async function loadRemoteModuleWithRetry(moduleName) {
  let lastError;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const c = await initContainer();
      const factory = await c.get(moduleName);
      const mod = await factory();
      loadFailed = false;
      return mod;
    } catch (err) {
      lastError = err;
      console.warn(
        `[Konitys Federation] Attempt ${attempt + 1}/${MAX_RETRIES + 1} failed for ${moduleName}:`,
        err.message
      );
      // Reset for retry
      container = null;
    }
  }
  // All retries exhausted
  loadFailed = true;
  console.error(
    `[Konitys Federation] Failed to load ${moduleName} after ${MAX_RETRIES + 1} attempts.`,
    lastError
  );
  startReconnect();
  return null;
}

/**
 * Load a federated component, returning either the real component
 * or a { default: FallbackComponent } module for React.lazy().
 *
 * Usage:
 *   const RemoteHeader = lazy(() => loadRemoteComponent('./HeaderBar', FallbackHeaderBar));
 */
export async function loadRemoteComponent(moduleName, FallbackComponent) {
  const mod = await loadRemoteModuleWithRetry(moduleName);
  if (mod) {
    // Vite federation factory returns the component directly (not a module object),
    // so we need to wrap it for React.lazy() which expects { default: Component }
    return mod.default ? mod : { default: mod };
  }
  // Return fallback as a lazy-compatible module
  if (FallbackComponent) {
    return { default: FallbackComponent };
  }
  // Last resort — empty placeholder
  return { default: () => null };
}

/**
 * Check if the federation is currently in failed/fallback state.
 */
export function isFederationFailed() {
  return loadFailed;
}
